import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getCurrentUser, getFounderAgreementStatus } from '@/lib/api/auth';
import {
  FOUNDER_AGREEMENT_ACCESS_BLOCKED_EVENT,
  readFounderAgreementGateState,
} from '@/lib/founderAgreementGate';
import { FounderAgreementModal } from '@/components/auth/FounderAgreementModal';
import { isDemoModeEnabled } from '@/demo/config';

const PUBLIC_PATH_PREFIXES = [
  '/login',
  '/signup',
  '/forgot-password',
  '/auth/consent-required',
  '/auth/subscription-required',
  '/client-consent',
  '/questionnaires/share/',
  '/book/',
  '/reschedule/',
  '/terms',
  '/privacy',
  '/founding-circle-agreement',
  '/blog/',
  '/start',
];

const shouldRefreshFounderAgreementStatus = (pathname: string): boolean => {
  if (PUBLIC_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(prefix))) {
    return false;
  }

  return true;
};

const isFounderAgreementAccessBlocked = (): boolean =>
  Boolean(readFounderAgreementGateState()?.accessBlocked);

const shouldShowFounderAgreementGate = (pathname: string): boolean =>
  shouldRefreshFounderAgreementStatus(pathname) && isFounderAgreementAccessBlocked();

export const FounderAgreementGateBridge = () => {
  const location = useLocation();
  const [modalOpen, setModalOpen] = useState(false);
  const isDemoMode = isDemoModeEnabled();

  useEffect(() => {
    if (isDemoMode) return;
    const handleFounderAgreementBlocked = () => {
      if (shouldShowFounderAgreementGate(location.pathname)) {
        setModalOpen(true);
      }
    };

    window.addEventListener(FOUNDER_AGREEMENT_ACCESS_BLOCKED_EVENT, handleFounderAgreementBlocked);
    return () => {
      window.removeEventListener(
        FOUNDER_AGREEMENT_ACCESS_BLOCKED_EVENT,
        handleFounderAgreementBlocked
      );
    };
  }, [isDemoMode, location.pathname]);

  useEffect(() => {
    if (isDemoMode) return;
    setModalOpen(shouldShowFounderAgreementGate(location.pathname));
  }, [isDemoMode, location.pathname, location.search]);

  useEffect(() => {
    if (isDemoMode) return;
    const currentUser = getCurrentUser();
    if (!currentUser?.token || !shouldRefreshFounderAgreementStatus(location.pathname)) {
      return;
    }

    let isCancelled = false;

    const refreshFounderAgreementStatus = async () => {
      try {
        await getFounderAgreementStatus();
        if (isCancelled) {
          return;
        }

        setModalOpen(isFounderAgreementAccessBlocked());
      } catch {
        // Protected API failures are handled by the HTTP interceptor.
      }
    };

    void refreshFounderAgreementStatus();

    return () => {
      isCancelled = true;
    };
  }, [isDemoMode, location.pathname, location.search]);

  if (isDemoMode) {
    return null;
  }

  return (
    <FounderAgreementModal
      open={modalOpen}
      onAccepted={() => setModalOpen(false)}
    />
  );
};
